import React from "react";
import { Box, Typography, Divider } from "@mui/material";

import cargoAirportsIconSvg from "../../assets/icons/flight_yellow_24dp.svg";
import portsIconSvg from "../../assets/icons/anchor_white_24dp.svg";
import truckParkingIconSvg from "../../assets/icons/local_parking_white_on_green_24dp.svg";
import trainIconSvg from "../../assets/icons/train_FILL0_wght400_GRAD0_opsz48.svg";
import airportIconSvg from "../../assets/icons/flight_FILL0_wght400_GRAD0_opsz48.svg";
import countryIcon from "../../assets/icons/countrywideFlight.svg";

// Shared by the legend and the map's image loader, so the names and icons always match.
export const POINT_LAYERS = [
  { key: "showCargoAirports", imageId: "pin", label: "Cargo Airports", icon: cargoAirportsIconSvg, size: 15 },
  { key: "showPorts", imageId: "ports", label: "Ports", icon: portsIconSvg, size: 15 },
  { key: "showTruckParking", imageId: "truckParking", label: "Truck Parking", icon: truckParkingIconSvg, size: 15 },
  { key: "showRailDwellTimes", imageId: "railDwellTImes", label: "Rail Dwell Times", icon: trainIconSvg, size: 25 },
  { key: "showAirportDelay", imageId: "airportDelay", label: "Airport Delay (Texas)", icon: airportIconSvg, size: 25 },
  { key: "showCountrywideAirportDelay", imageId: "countrywideAirportDelay", label: "Countrywide Airport Delay", icon: countryIcon, size: 25 },
];

const PERFORMANCE_LABELS = {
  trkdelay: "Truck Delay (person-hours)",
  trkpti95: "Reliability (PTI-95)",
};

const compact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 2,
});
const fmt = (v) => (Number.isFinite(v) ? compact.format(v) : "–");

const SectionTitle = ({ children }) => (
  <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{children}</Typography>
);

const LegendIcon = ({ src }) => (
  // Dark chip so the white icons (ports, truck parking) stay visible
  <Box
    component="span"
    sx={{
      width: 22,
      height: 22,
      mr: 1,
      borderRadius: "4px",
      bgcolor: "#616161",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <img src={src} alt="" width={16} height={16} />
  </Box>
);

const GradientScale = ({ title, colors, min, max }) => (
  <Box sx={{ mb: 1 }}>
    <Typography sx={{ fontSize: 11 }}>{title}</Typography>
    <Box
      sx={{
        height: 10,
        borderRadius: 1,
        background: `linear-gradient(to right, ${colors.join(", ")})`,
      }}
    />
    <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
      <span>{fmt(min)}</span>
      <span>{fmt((min + max) / 2)}</span>
      <span>{fmt(max)}</span>
    </Box>
  </Box>
);

const MapLegend = ({ mapParameters, commodityInfo }) => {
  const {
    barColor,
    barHeight,
    roadwayPerformance,
    selectedCommoditiesCategory,
    colorScheme,
  } = mapParameters;

  const isValue = selectedCommoditiesCategory === "Route_valu";
  const commodityKey = isValue ? "values" : "tons";

  const ranges = {
    commodities: {
      label: isValue ? "Commodity Value ($)" : "Commodity Tonnage",
      min: commodityInfo?.minCommodity[commodityKey],
      max: commodityInfo?.maxCommodity[commodityKey],
    },
    roadwayPerformance: {
      label: PERFORMANCE_LABELS[roadwayPerformance],
      min: commodityInfo?.minRoadPerf[roadwayPerformance],
      max: commodityInfo?.maxRoadPerf[roadwayPerformance],
    },
  };
  const colorRange = ranges[barColor];
  const heightRange = ranges[barHeight];

  const showCorridors =
    mapParameters.showRoads === "visible" &&
    mapParameters.showCountrywideAirportDelay !== "visible";
  const activePointLayers = POINT_LAYERS.filter(
    (layer) => mapParameters[layer.key] === "visible"
  );
  const showDelayLines = mapParameters.showDelayLines === "visible";
  const showAis = mapParameters.showAisData2021 === "visible";

  if (!showCorridors && !activePointLayers.length && !showDelayLines && !showAis) {
    return null;
  }

  const sections = [];

  if (showCorridors) {
    sections.push(
      <Box key="corridors">
        <SectionTitle>Commodity Corridors- {commodityInfo?.name}</SectionTitle>
        <GradientScale
          title={`Bar color: ${colorRange.label}`}
          colors={colorScheme}
          min={colorRange.min}
          max={colorRange.max}
        />
        <Typography sx={{ fontSize: 11 }}>
          Bar height: {heightRange.label}
          <br />
          Taller bars = higher values ({fmt(heightRange.min)} - {fmt(heightRange.max)})
        </Typography>
      </Box>
    );
  }

  if (showDelayLines) {
    sections.push(
      <Box key="delay-lines">
        <SectionTitle>Airport Delay Routes</SectionTitle>
        <GradientScale
          title="Line color: Avg. arrival delay (min)"
          colors={["rgb(122, 193, 67)", "rgb(237, 28, 36)"]}
          min={20}
          max={80}
        />
        <Typography sx={{ fontSize: 11 }}>Line width: Avg. departure delay (min)</Typography>
        {[
          { label: "20", width: 2 },
          { label: "50", width: 6 },
          { label: "80+", width: 10 },
        ].map(({ label, width }) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", fontSize: 11 }}>
            <Box sx={{ width: 40, height: width, bgcolor: "rgba(3, 170, 238, 0.8)", mr: 1, borderRadius: 1 }} />
            {label}
          </Box>
        ))}
      </Box>
    );
  }

  if (activePointLayers.length || showAis) {
    sections.push(
      <Box key="layers">
        <SectionTitle>Layers</SectionTitle>
        {activePointLayers.map((layer) => (
          <Box key={layer.key} sx={{ display: "flex", alignItems: "center", mb: 0.5, fontSize: 12 }}>
            <LegendIcon src={layer.icon} />
            {layer.label}
          </Box>
        ))}
        {showAis && (
          <Box sx={{ display: "flex", alignItems: "center", fontSize: 12 }}>
            <Box
              component="span"
              sx={{
                width: 22,
                height: 22,
                mr: 1,
                borderRadius: "4px",
                background: "linear-gradient(135deg, #440154, #21908d, #fde725)",
              }}
            />
            AIS Vessel Transit Counts (2021)
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      id="commodity-legend"
      sx={{
        position: "absolute",
        bottom: 30,
        left: 10,
        zIndex: 5,
        bgcolor: "rgba(255, 255, 255, 0.95)",
        borderRadius: 1,
        boxShadow: 3,
        p: 1.5,
        width: 240,
        maxHeight: "60%",
        overflowY: "auto",
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Legend</Typography>
      {sections.map((section, i) => (
        <React.Fragment key={section.key}>
          {i > 0 && <Divider sx={{ my: 1 }} />}
          {section}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default MapLegend;